/**
 * Windowed pagination dots for the product gallery (desktop).
 *
 * The theme renders one dot per slide, which overflows when a product has many
 * images. We always show a fixed-size window of dots (WINDOW). CSS enforces the
 * first-7 cap from the very first paint (no flash while loading); this script
 * then slides the 7-dot window so the active dot stays visible and pinned toward
 * the edge you're moving toward:
 *   - moving right: active stays at the right edge until the last dot is reached
 *   - moving left:  active stays at the left edge until the first dot is reached
 *
 * It toggles the `dot-in-window` class on the visible <li>s and sets
 * `data-windowed` on the list so CSS shows exactly that window.
 */
(() => {
  const WINDOW = 7;

  /** @param {HTMLElement} list - the <ol class="slideshow-controls__dots"> */
  const initList = (list) => {
    if (list.dataset.initWindowed === 'true') return;
    list.dataset.initWindowed = 'true';

    /** @returns {HTMLLIElement[]} */
    const items = () => Array.from(list.querySelectorAll(':scope > li'));

    const activeIndex = () => {
      const dots = items();
      return dots.findIndex((li) => {
        const btn = li.querySelector('button');
        return btn && btn.getAttribute('aria-selected') === 'true';
      });
    };

    let windowStart = 0;

    const apply = () => {
      const dots = items();
      const total = dots.length;

      // Everything fits — no windowing needed.
      if (total <= WINDOW) {
        delete list.dataset.windowed;
        dots.forEach((li) => li.classList.remove('dot-in-window'));
        windowStart = 0;
        return;
      }

      const active = activeIndex();
      if (active < 0) return;

      // Keep one dot of buffer at the leading edge while more dots remain in
      // that direction, so the active dot sits one-before-the-last visible one
      // (signalling "there's more"). Only when you reach the very first/last dot
      // does the active sit flush at the edge.
      const BUFFER = 1;

      // Moving right past the window: keep the active at the second-to-last slot.
      if (active > windowStart + WINDOW - 1 - BUFFER) {
        windowStart = active - WINDOW + 1 + BUFFER;
      }
      // Moving left past the window: keep the active at the second slot.
      if (active < windowStart + BUFFER) {
        windowStart = active - BUFFER;
      }
      // Clamp so the window never runs past either end (flush at the extremes).
      windowStart = Math.max(0, Math.min(windowStart, total - WINDOW));

      const windowEnd = windowStart + WINDOW - 1;
      list.dataset.windowed = 'true';
      dots.forEach((li, i) => {
        li.classList.toggle('dot-in-window', i >= windowStart && i <= windowEnd);
      });
    };

    const observer = new MutationObserver(apply);
    observer.observe(list, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-selected'],
    });

    apply();
  };

  const initAll = () => {
    document
      .querySelectorAll('media-gallery .slideshow-controls__dots')
      .forEach((list) => initList(/** @type {HTMLElement} */ (list)));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
